import { useEffect, useMemo, useRef, useState } from 'react'
import { FormGrid, Button, Callout, Card, CardContent, CardHeader, Checkbox, DataTable, LoadingState, Select, StatusPill, TextField } from './ui'
import {
  CONTRACT_FIELDS_CHANGED_EVENT,
  contractFieldsApi,
  dynamicFieldsApi,
  fieldErrorsFrom,
  resolveVisibility,
  type ContractField,
  type ContractFieldContext,
  type CustomerPrefillPreview,
  type DynamicFieldDefinition,
  type FieldError,
  type FieldValue,
} from '../api/dynamicFields'
import { useFeatureFlag } from '../lib/featureFlags'
import { initialEditValue, requiredMessage } from '../lib/dynamicFieldRules'
import { DynamicFileSelect } from './DynamicFileSelect'
import { SignaturePad } from './SignaturePad'

/**
 * F-074 (TASK-0328, REQ-042-001/002): campi dinamici della pratica.
 *
 * - Con `contractId` (dettaglio): carica i campi applicabili alla pratica con i valori salvati, mostra
 *   quelli visibili e salva solo quelli modificati. Il server ricalcola i campi calcolati e rifiuta i
 *   valori non validi (400, errore per campo).
 * - Senza `contractId` (nuova pratica): mostra i campi applicabili al settore e al prodotto scelti e
 *   restituisce i valori compilati al genitore (`onChange`), che li salva dopo la creazione.
 *
 * Le condizioni di visualizzazione si valutano live; il server le rivaluta al salvataggio. I campi di tipo
 * Importo arrivano solo a chi ha CanSeeRealAmounts (per gli altri non ci sono nella risposta).
 */
interface Props {
  contractId?: number
  context: ContractFieldContext
  /** Nuova pratica: valori compilati dei campi visibili (testi, booleani, elenchi), senza i vuoti. */
  onChange?: (values: Record<string, FieldValue>) => void
  /** Sola lettura (es. pratica non piu' modificabile). */
  readOnly?: boolean
  onSaved?: () => void
}

type EditValue = string | boolean | string[]

function fromServer(f: ContractField): EditValue {
  const v = f.value
  if (f.type === 'Casella') return v === true
  if (f.type === 'ElencoMultiplo') return Array.isArray(v) ? v : []
  if (v === null || v === undefined) return ''
  return String(v)
}

function isEmpty(v: EditValue | undefined): boolean {
  if (v === undefined) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  return false
}

/** Valore da inviare: i vuoti diventano `null` (svuota), le Caselle restano booleani. */
function toServer(f: Pick<ContractField, 'type'>, v: EditValue | undefined): FieldValue {
  if (f.type === 'Casella') return v === true
  if (isEmpty(v)) return null
  return v as string | string[]
}

function fromDefinition(d: DynamicFieldDefinition): ContractField {
  return {
    code: d.code,
    label: d.label,
    section: d.section,
    sortOrder: d.sortOrder,
    type: d.type,
    required: d.required,
    options: d.options,
    validation: d.validation,
    displayCondition: d.displayCondition,
    externalMappingLabel: d.externalMappingLabel,
    defaultValue: d.defaultValue ?? null,
    version: d.version,
    visible: true,
    value: null,
    valueVersion: null,
  }
}

export function ContractDynamicFields({ contractId, context, onChange, readOnly = false, onSaved }: Props) {
  const isNew = contractId === undefined
  const [fields, setFields] = useState<ContractField[]>([])
  const [values, setValues] = useState<Record<string, EditValue>>({})
  const [dirty, setDirty] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  // R3-12 (REQ-041-001), flag `pratiche.campi_condizionali`: valori predefiniti e validazione all'uscita dal campo.
  const rulesOn = useFeatureFlag('pratiche.campi_condizionali')
  const [blurErrors, setBlurErrors] = useState<Record<string, string>>({})
  const defaultsApplied = useRef<Set<string>>(new Set())
  const [savedAt, setSavedAt] = useState<boolean>(false)
  // F-091: un altro componente (es. il riporto dal questionario) ha cambiato i campi della pratica.
  const [reloadTick, setReloadTick] = useState(0)
  // F-098: "Precompila dall'anagrafica" -> anteprima -> conferma.
  const [cpPreview, setCpPreview] = useState<CustomerPrefillPreview | null>(null)
  const [cpBusy, setCpBusy] = useState(false)
  const [cpMsg, setCpMsg] = useState<{ kind: 'success' | 'danger' | 'info'; text: string } | null>(null)

  useEffect(() => {
    if (contractId === undefined) return
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ contractId?: number }>).detail
      if (detail?.contractId === undefined || detail.contractId === contractId) setReloadTick(t => t + 1)
    }
    window.addEventListener(CONTRACT_FIELDS_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(CONTRACT_FIELDS_CHANGED_EVENT, onChanged)
  }, [contractId])

  // Chiave d'ambito: al suo cambio si ricarica l'elenco dei campi.
  const scopeKey = [context.departmentId ?? '', context.operatorId ?? '', context.productId ?? '', context.customerSegment ?? ''].join('|')
  const ready = isNew ? context.departmentId != null && context.productId != null : true

  useEffect(() => {
    let cancelled = false
    if (!ready) {
      setFields([])
      setValues({})
      return
    }
    setLoading(true)
    setError(null)
    const load: Promise<ContractField[]> = isNew
      ? dynamicFieldsApi
          .listActive({
            departmentId: context.departmentId as number,
            productId: context.productId as number,
            ...(context.operatorId != null ? { operatorId: context.operatorId } : {}),
            ...(context.customerSegment ? { customerSegment: context.customerSegment } : {}),
          })
          .then(items => items.map(fromDefinition))
      : contractFieldsApi.get(contractId as number).then(r => r.fields)
    load
      .then(list => {
        if (cancelled) return
        setFields(list)
        // Nella nuova pratica si tengono i valori gia' digitati dei campi che restano; nel dettaglio si riparte dal server.
        setValues(prev => {
          const next: Record<string, EditValue> = {}
          for (const f of list) next[f.code] = isNew && f.code in prev ? (prev[f.code] as EditValue) : fromServer(f)
          return next
        })
        setDirty(new Set())
        setFieldErrors([])
      })
      .catch(e => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore nel caricamento dei campi')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, scopeKey, ready, reloadTick])

  // Nuova pratica, flag acceso: i campi vuoti partono dal valore predefinito, una sola volta per campo
  // (chi svuota il campo a mano non se lo vede rimettere). Il flag arriva dopo i campi: per questo e' un effetto.
  useEffect(() => {
    if (!isNew || !rulesOn || fields.length === 0) return
    const todo = fields.filter(f => f.defaultValue != null && !defaultsApplied.current.has(f.code))
    if (todo.length === 0) return
    for (const f of todo) defaultsApplied.current.add(f.code)
    setValues(prev => {
      const next = { ...prev }
      for (const f of todo) {
        const blank = f.type === 'Casella' ? next[f.code] !== true : isEmpty(next[f.code])
        if (blank) next[f.code] = initialEditValue(f.type, f.defaultValue)
      }
      return next
    })
  }, [fields, isNew, rulesOn])

  const asFieldValues = useMemo(() => {
    const out: Record<string, FieldValue> = {}
    for (const f of fields) {
      const v = values[f.code]
      out[f.code] = f.type === 'Casella' ? v === true : isEmpty(v) ? null : (v as FieldValue)
    }
    return out
  }, [fields, values])

  // La chiave del contesto, non l'oggetto: il genitore ne crea uno nuovo a ogni render.
  const contextKey = JSON.stringify(context)
  const visibility = useMemo(
    () => resolveVisibility(fields, context, asFieldValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, contextKey, asFieldValues],
  )

  // Nuova pratica: il genitore riceve i soli valori compilati dei campi visibili e non calcolati.
  useEffect(() => {
    if (!isNew || !onChange) return
    const out: Record<string, FieldValue> = {}
    for (const f of fields) {
      if (f.type === 'Calcolato' || !visibility[f.code]) continue
      const v = values[f.code]
      if (f.type === 'Casella') {
        if (v === true) out[f.code] = true
      } else if (!isEmpty(v)) {
        out[f.code] = v as FieldValue
      }
    }
    onChange(out)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, values, visibility, isNew])

  function setValue(code: string, v: EditValue) {
    setValues(prev => ({ ...prev, [code]: v }))
    setBlurErrors(prev => {
      if (!(code in prev)) return prev
      const { [code]: _gone, ...rest } = prev
      return rest
    })
    setDirty(prev => new Set(prev).add(code))
    setSavedAt(false)
  }

  /** R3-12: all'uscita dal campo, "obbligatorio" in locale e poi le stesse regole del salvataggio dal server. */
  async function validateOnBlur(f: ContractField) {
    if (!rulesOn || readOnly || f.type === 'Calcolato' || f.type === 'File' || f.type === 'Firma') return
    const v = values[f.code]
    const setMsg = (msg: string | null) =>
      setBlurErrors(prev => {
        if (msg === null) {
          if (!(f.code in prev)) return prev
          const { [f.code]: _gone, ...rest } = prev
          return rest
        }
        return { ...prev, [f.code]: msg }
      })
    const missing = requiredMessage(f, v, visibility[f.code] !== false)
    if (missing) return setMsg(missing)
    if (isEmpty(v) && f.type !== 'Casella') return setMsg(null)
    try {
      const r = await dynamicFieldsApi.check(f.code, toServer(f, v))
      setMsg(r.valid ? null : (r.error ?? 'Valore non valido.'))
    } catch {
      setMsg(null) // errore di rete: il controllo vero resta quello del salvataggio
    }
  }

  async function save() {
    if (isNew || contractId === undefined) return
    setSaving(true)
    setError(null)
    setFieldErrors([])
    try {
      const body: Record<string, FieldValue> = {}
      for (const f of fields) {
        if (!dirty.has(f.code) || f.type === 'Calcolato') continue
        body[f.code] = visibility[f.code] ? toServer(f, values[f.code]) : null
      }
      const res = await contractFieldsApi.save(contractId, body)
      setFields(res.fields)
      const next: Record<string, EditValue> = {}
      for (const f of res.fields) next[f.code] = fromServer(f)
      setValues(next)
      setDirty(new Set())
      setSavedAt(true)
      onSaved?.()
    } catch (e) {
      const list = fieldErrorsFrom(e)
      setFieldErrors(list)
      setError(list.length > 0 ? 'Alcuni valori non sono validi.' : e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function openCustomerPreview() {
    if (contractId === undefined) return
    setCpBusy(true)
    setCpMsg(null)
    try {
      setCpPreview(await contractFieldsApi.customerPrefillPreview(contractId))
    } catch (e) {
      setCpMsg({ kind: 'danger', text: e instanceof Error ? e.message : "Errore nel calcolo dell'anteprima" })
    } finally {
      setCpBusy(false)
    }
  }

  async function confirmCustomerPrefill() {
    if (contractId === undefined) return
    setCpBusy(true)
    setCpMsg(null)
    try {
      const r = await contractFieldsApi.customerPrefillApply(contractId)
      setCpPreview(null)
      setCpMsg({
        kind: r.written.length > 0 ? 'success' : 'info',
        text: r.written.length > 0 ? `Precompilati ${r.written.length} campi dall'anagrafica.` : 'Nessun campo da precompilare.',
      })
      if (r.written.length > 0) {
        window.dispatchEvent(new CustomEvent(CONTRACT_FIELDS_CHANGED_EVENT, { detail: { contractId } }))
      }
    } catch (e) {
      setCpMsg({ kind: 'danger', text: e instanceof Error ? e.message : 'Errore durante la precompilazione' })
    } finally {
      setCpBusy(false)
    }
  }

  const visibleFields = fields.filter(f => visibility[f.code] !== false)
  const sections = useMemo(() => {
    const map = new Map<string, ContractField[]>()
    for (const f of visibleFields) {
      const key = f.section ?? 'Altri dati'
      map.set(key, [...(map.get(key) ?? []), f])
    }
    return [...map.entries()]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, visibility])

  if (isNew && !ready) {
    return null
  }
  if (loading) {
    return <LoadingState label="Caricamento dei campi della pratica..." />
  }
  if (!error && visibleFields.length === 0) {
    // Nessun campo applicabile: nel dettaglio non si occupa spazio.
    return null
  }

  const errorOf = (code: string) => fieldErrors.find(e => e.code === code)?.message ?? blurErrors[code]

  return (
    <Card variant="outlined" className="mb-3" data-testid="contract-dynamic-fields">
      <CardHeader
        title="Dati aggiuntivi della pratica"
        actions={
          !isNew && !readOnly ? (
            <>
              <Button
                type="button"
                variant="outlined"
                size="sm"
                disabled={cpBusy || saving || dirty.size > 0}
                title={dirty.size > 0 ? 'Salva prima i dati modificati' : 'Riporta nei campi vuoti i dati dell\'anagrafica'}
                onClick={() => void openCustomerPreview()}
              >
                {cpBusy && !cpPreview ? 'Calcolo...' : 'Precompila dall\'anagrafica'}
              </Button>
              <Button
                type="button"
                variant="tonal"
                size="sm"
                disabled={saving || dirty.size === 0}
                onClick={() => void save()}
              >
                {saving ? 'Salvataggio...' : 'Salva i dati'}
              </Button>
            </>
          ) : undefined
        }
      />
      <CardContent>
        {cpMsg && <Callout tone={cpMsg.kind === 'danger' ? 'error' : cpMsg.kind} role="status">{cpMsg.text}</Callout>}
        {cpPreview && (
          <Card as="div" variant="filled" padding="sm" className="mb-3">
            <div className="md-typescale-emphasized-body-medium mb-1">Anteprima della precompilazione dall&apos;anagrafica</div>
            {!cpPreview.canApply && (
              <Callout tone="warning">
                In questo stato i campi della pratica non si possono modificare: l&apos;anteprima è solo informativa.
              </Callout>
            )}
            {cpPreview.emptyReason && <div className="md-typescale-body-small text-muted mb-2">Nessun dato: {cpPreview.emptyReason}.</div>}
            {!cpPreview.emptyReason && cpPreview.items.length === 0 && (
              <div className="md-typescale-body-small text-muted mb-2">
                Nessun campo della pratica ha un&apos;origine anagrafica configurata (Impostazioni, Campi della pratica).
              </div>
            )}
            {cpPreview.items.length > 0 && (
              <div className="mb-2">
                <DataTable variant="plain" caption="Anteprima della precompilazione dall&apos;anagrafica">
                  <thead>
                    <tr><th scope="col">Campo</th><th scope="col">Origine</th><th scope="col">Esito</th></tr>
                  </thead>
                  <tbody>
                    {cpPreview.items.map(i => (
                      <tr key={i.fieldCode} className={i.apply ? undefined : 'text-muted'}>
                        <td>{i.fieldLabel}</td>
                        <td>{i.attributeLabel}</td>
                        <td>
                          {i.apply
                            ? <><StatusPill tone="success">Verrà scritto</StatusPill> {i.value}</>
                            : <>Saltato: {i.skipReason}</>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </div>
            )}
            <div className="d-flex gap-2">
              <Button
                type="button" variant="tonal" size="sm"
                disabled={cpBusy || !cpPreview.canApply || cpPreview.items.filter(i => i.apply).length === 0}
                onClick={() => void confirmCustomerPrefill()}
              >
                {cpBusy ? 'Salvataggio...' : `Conferma (${cpPreview.items.filter(i => i.apply).length})`}
              </Button>
              <Button type="button" variant="text" size="sm" disabled={cpBusy} onClick={() => setCpPreview(null)}>
                Annulla
              </Button>
            </div>
          </Card>
        )}
        {error && <Callout tone="error">{error}</Callout>}
        {savedAt && <Callout tone="success">Dati salvati.</Callout>}
        {sections.map(([section, list]) => (
          <div key={section} className="mb-3">
            {sections.length > 1 && <h3 className="md-typescale-title-small text-muted">{section}</h3>}
            <FormGrid>
              {list.map(f => (
                <div
                  key={f.code}
                  className={isWideField(f) ? 'md-span-full' : undefined}
                  onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) void validateOnBlur(f) }}
                >
                  <FieldEditor
                    field={f}
                    value={values[f.code]}
                    disabled={readOnly}
                    error={errorOf(f.code)}
                    onChange={v => setValue(f.code, v)}
                  />
                </div>
              ))}
            </FormGrid>
          </div>
        ))}
        {isNew && <p className="text-muted md-typescale-body-small mb-0">I dati compilati vengono salvati insieme alla pratica.</p>}
      </CardContent>
    </Card>
  )
}

/** Testi lunghi (oltre 200 caratteri) e gruppi di caselle occupano l'intera riga della griglia. */
function isWideField(f: ContractField): boolean {
  if (f.type === 'Firma') return true
  return f.type === 'ElencoMultiplo' || (f.type === 'Testo' && (f.validation?.maxLength ?? 0) > 200)
}

function FieldEditor({
  field,
  value,
  disabled,
  error,
  onChange,
}: {
  field: ContractField
  value: EditValue | undefined
  disabled: boolean
  error: string | undefined
  onChange: (v: EditValue) => void
}) {
  const id = `df-${field.code}`
  const label = `${field.label}${field.required ? ' *' : ''}`
  const str = typeof value === 'string' ? value : ''
  const v = field.validation ?? {}
  const step = v.decimals !== undefined ? (v.decimals === 0 ? '1' : String(Math.pow(10, -v.decimals))) : 'any'

  switch (field.type) {
    case 'Casella':
      return (
        <div>
          <Checkbox
            id={id}
            label={label}
            checked={value === true}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
          />
          {error && <div className="text-danger md-typescale-body-small">{error}</div>}
        </div>
      )
    case 'Elenco':
      return (
        <Select id={id} label={label} error={error} value={str} disabled={disabled} onChange={e => onChange(e.target.value)}>
          <option value="">—</option>
          {field.options.map(o => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      )
    case 'ElencoMultiplo': {
      const picked = Array.isArray(value) ? value : []
      return (
        <div>
          <div className="md-typescale-body-small text-muted mb-1" id={`${id}-label`}>{label}</div>
          <div id={id} role="group" aria-labelledby={`${id}-label`}>
            {field.options.map(o => (
              <Checkbox
                key={o}
                id={`${id}-${o}`}
                label={o}
                disabled={disabled}
                checked={picked.includes(o)}
                onChange={e => onChange(e.target.checked ? [...picked, o] : picked.filter(x => x !== o))}
              />
            ))}
          </div>
          {error && <div className="text-danger md-typescale-body-small">{error}</div>}
        </div>
      )
    }
    case 'Numero':
    case 'Importo':
    case 'Percentuale':
      return (
        <TextField
          id={id}
          label={label}
          error={error}
          type="number"
          value={str}
          step={step}
          {...(v.min !== undefined ? { min: v.min } : {})}
          {...(v.max !== undefined ? { max: v.max } : {})}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'Data':
      return <TextField id={id} label={label} error={error} type="date" value={str} disabled={disabled} onChange={e => onChange(e.target.value)} />
    case 'Email':
      return <TextField id={id} label={label} error={error} type="email" value={str} disabled={disabled} onChange={e => onChange(e.target.value)} />
    case 'Telefono':
      return <TextField id={id} label={label} error={error} type="tel" value={str} disabled={disabled} onChange={e => onChange(e.target.value)} />
    case 'CodiceFiscale':
      return (
        <TextField
          id={id}
          label={label}
          error={error}
          type="text"
          value={str}
          maxLength={16}
          uppercase
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'PartitaIva':
      return <TextField id={id} label={label} error={error} type="text" value={str} maxLength={13} disabled={disabled} onChange={e => onChange(e.target.value)} />
    case 'Iban':
      return (
        <TextField
          id={id}
          label={label}
          error={error}
          type="text"
          value={str}
          maxLength={42}
          uppercase
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
        />
      )
    case 'File':
      return <DynamicFileSelect id={id} label={label} error={error} fileRef={field.fileRef} value={str} disabled={disabled} onChange={onChange} />
    case 'Firma':
      return <SignaturePad id={id} label={label} error={error} value={str} disabled={disabled} onChange={onChange} />
    case 'Calcolato':
      return (
        <TextField
          id={id}
          label={label}
          type="text"
          value={str === '' ? '' : str}
          supportingText="Calcolato dal sistema al salvataggio"
          readOnly
          disabled
        />
      )
    default:
      return (
        <TextField
          id={id}
          label={label}
          error={error}
          type="text"
          value={str}
          {...(v.maxLength !== undefined ? { maxLength: v.maxLength } : {})}
          disabled={disabled}
          onChange={e => onChange(e.target.value)}
        />
      )
  }
}
